import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Lock, Mail, ToggleLeft, ToggleRight, CheckCircle } from 'lucide-react';

const EmailConfigForm = ({ token, API_URL }) => {
    const [config, setConfig] = useState({
        email: '',
        appPassword: '',
        isActive: false
    });
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/email-config`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data) setConfig(res.data);
        } catch (err) {
            console.error("Error fetching email config", err);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/admin/email-config`, config, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setConfig(res.data);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            alert("Failed to save email configuration");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSave} className="space-y-6 mt-6">
            <div className="flex items-center gap-4 mb-6">
                <button
                    type="button"
                    onClick={() => setConfig({ ...config, isActive: !config.isActive })}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold transition-all ${config.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                    {config.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                    {config.isActive ? 'Email Billing Enabled' : 'Email Billing Disabled'}
                </button>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Gmail Address
                </label>
                <div className="relative">
                    <Mail className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    <input
                        type="email"
                        className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-blue-500 outline-none"
                        value={config.email}
                        onChange={e => setConfig({ ...config, email: e.target.value })}
                        placeholder="yourrestaurant@gmail.com"
                        required={config.isActive}
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Google App Password
                </label>
                <div className="relative">
                    <Lock className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    <input
                        type="password"
                        className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-blue-500 outline-none"
                        value={config.appPassword}
                        onChange={e => setConfig({ ...config, appPassword: e.target.value })}
                        placeholder="16-character generated app password"
                        required={config.isActive}
                    />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Generate an App Password from your Google Account settings: Security {'>'} 2-Step Verification {'>'} App Passwords. Do not use your normal account password.
                </p>
            </div>

            <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all flex justify-center items-center gap-2"
            >
                {loading ? 'Saving...' : saved ? <><CheckCircle size={20} /> Saved!</> : <><Save size={20} /> Save Configuration</>}
            </button>
        </form>
    );
};

export default EmailConfigForm;
